import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  applyStyle, readStyle, toHex, toPx, describe, ancestorTrail,
  removeElement, duplicateElement, holdsOnlyText,
  isImage, replaceImage, uploadImage,
  isVideo, isEmbed, setVideo, setEmbed, hasControls, setVideoControls,
} from '@/lib/visualEdit';
import { uploadVideo, VIDEO_ACCEPT, MAX_VIDEO_MB } from '@/lib/mediaUpload';
import { videoEmbed } from '@/lib/videoEmbed';
import {
  AlignLeft, AlignCenter, AlignRight, Bold, Trash2, Copy,
  CornerUpLeft, Link2, Image as ImageIcon, X, SlidersHorizontal, Pencil, Upload, Film,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { authFetch } from '@/lib/authFetch';

// A link that is plainly a video file, for turning a picture into a video.
const VIDEO_FILE_LINK = /^https?:\/\/\S+\.(mp4|m4v|webm|mov)(\?\S*)?$/i;

// The controls for the current selection. Every one writes an inline style
// straight onto the node in the preview document — no request, no credit, no
// round trip. Deliberately dumb: it reads computed values on selection and
// writes on change, and the frame owns everything else.
//
// Shape matters here as much as behaviour. This used to be a docked panel at
// `max-h-[46%]`, so selecting anything swallowed half the preview — you lost
// sight of the thing you were editing at the exact moment you were editing it.
// It is now one row: the handful of controls people reach for constantly sit on
// the bar, and everything else lives one click away in a popover. Same
// capabilities, roughly a fifth of the height.

const FIELD = 'w-full bg-zinc-950 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-100 ' +
              'focus:outline-none focus:border-orange-500';
const LABEL = 'text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block';
const ICON_BTN = 'p-1.5 rounded-md border border-zinc-800 text-zinc-400 hover:text-white ' +
                 'hover:border-zinc-600 transition-colors shrink-0';
const SWATCH = 'w-7 h-7 rounded-md border border-zinc-700 cursor-pointer bg-zinc-950 shrink-0 p-0.5';

export default function VisualEditBar({ element, onSelectElement, onChange, onClose }) {
  // Local mirror of the node's computed style, so inputs stay controlled.
  const [v, setV] = useState(/** @type {Record<string, any>} */ ({}));

  const read = useCallback((el) => {
    if (!el) return {};
    return {
      color:      toHex(readStyle(el, 'color')),
      background: toHex(readStyle(el, 'background-color')),
      fontSize:   toPx(readStyle(el, 'font-size')),
      fontWeight: readStyle(el, 'font-weight'),
      align:      readStyle(el, 'text-align'),
      padding:    toPx(readStyle(el, 'padding-top')),
      radius:     toPx(readStyle(el, 'border-radius')),
      href:       el.tagName === 'A' ? el.getAttribute('href') || '' : null,
      src:        el.tagName === 'IMG' ? el.getAttribute('src') || '' : null,
      text:       holdsOnlyText(el) ? (el.textContent || '') : null,
      videoSrc:   el.tagName === 'VIDEO'
        ? (el.getAttribute('src') || el.querySelector('source')?.getAttribute('src') || '')
        : isEmbed(el) ? el.getAttribute('src') : null,
      controls:   hasControls(el),
    };
  }, []);

  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState(null);   // a video's upload, 0–100
  const [imgError, setImgError]   = useState('');
  const [vidError, setVidError]   = useState('');

  useEffect(() => { setV(read(element)); }, [element, read]);
  // A new selection starts clean; an error about the last image is noise here.
  useEffect(() => { setImgError(''); setVidError(''); }, [element]);

  if (!element) {
    return (
      <div className="border-t border-zinc-800 bg-zinc-900 px-4 py-2.5 text-xs text-zinc-500">
        Click any element in the preview to edit it. Changes are free — they never use credits.
      </div>
    );
  }

  // Write a style and keep the local mirror in step.
  const set = (key, prop, value, unit = '') => {
    applyStyle(element, prop, value === '' ? '' : `${value}${unit}`);
    setV((cur) => ({ ...cur, [key]: value }));
    onChange?.();
  };

  const setAttr = (key, name, value) => {
    element.setAttribute(name, value);
    setV((cur) => ({ ...cur, [key]: value }));
    onChange?.();
  };

  // Swapping the picture is the edit people reach for most on a generated site
  // — the photographs are fetched by description, so one in ten is merely close
  // — and it used to be a text field for a URL, three clicks deep in the
  // popover, with no way to use a file you actually have. It is now on the bar
  // whenever an image is selected.
  const pickFile = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';                 // re-picking the same file must re-fire
    if (!file) return;
    setImgError('');
    setUploading(true);
    try {
      // Uploaded, not inlined: as a data: URL the picture's size was the
      // page's, and a 3.76 MB PNG failed its save as "pages exceeds the 2 MB
      // limit". See uploadImage.
      const url = await uploadImage(file, { request: authFetch });
      if (replaceImage(element, url)) {
        setV((cur) => ({ ...cur, src: url }));
        onChange?.();
      } else {
        setImgError('That image could not be applied.');
      }
    } catch (err) {
      setImgError(err.message || 'That image could not be uploaded.');
    } finally {
      setUploading(false);
    }
  };

  // A video is uploaded rather than inlined, exactly as an image is: a clip is
  // megabytes, and in html_content it would be re-sent on every edit. It goes
  // up as the request body itself (mediaUpload.js), which takes up to 100 MB
  // and an iPhone's .mov; as base64 in JSON it stopped at 4 MB, a few seconds
  // of footage, and that was the complaint: pictures could be replaced and
  // videos could not. A picture can become a video too, or a YouTube or Vimeo
  // player.
  const pickVideoFile = () => videoRef.current?.click();

  // A picture that became a video, or a video that became a player, is a new
  // node: the selection follows it, and the bar re-reads it.
  const finish = (next) => {
    onChange?.();
    if (next !== element) onSelectElement?.(next);
    else setV(read(next));
  };

  const onVideoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';                 // re-picking the same file must re-fire
    if (!file) return;
    setVidError('');
    setUploading(true);
    setProgress(0);
    try {
      const url = await uploadVideo(file, { onProgress: setProgress });
      // Deleted while it uploaded: there is nowhere left to put it.
      if (!element.isConnected) throw new Error('That spot was removed while the video uploaded.');
      const next = setVideo(element, url, { controls: isVideo(element) ? hasControls(element) : false });
      if (!next) throw new Error('That video could not be put in this spot.');
      finish(next);
    } catch (err) {
      setVidError(err.message || 'That video could not be uploaded.');
    } finally {
      setUploading(false);
      setProgress(null);
    }
  };

  // YouTube or Vimeo becomes their player. A link to a video file becomes a
  // video; on a video, any https:// address is taken as one, since a CDN's
  // need not end in .mp4, but a picture only turns into a video for a link
  // that plainly is one.
  const applyVideoLink = (raw) => {
    const link = String(raw || '').trim();
    // The field also applies on blur, and that can fire from a handler made
    // before the swap, holding the picture that is no longer in the page:
    // swapping that again built a second, detached player and selected it,
    // and the bar went on editing something nobody could see. So: nothing
    // for an element no longer in the page, or for a link already showing.
    if (!link || !element?.isConnected) return;
    const embed = videoEmbed(link);
    if (link === v.videoSrc || (embed && embed.src === v.videoSrc)) return;
    setVidError('');
    let next = null;
    if (embed) next = setEmbed(element, link);
    else if (VIDEO_FILE_LINK.test(link) || (isVideo(element) && /^https:\/\//i.test(link))) {
      next = setVideo(element, link, { controls: isVideo(element) ? hasControls(element) : false });
    }
    if (next) finish(next);
    else setVidError('Paste a YouTube or Vimeo link, or a full https:// link to an .mp4 or .webm file.');
  };

  const toggleControls = (on) => {
    if (setVideoControls(element, on)) {
      setV((cur) => ({ ...cur, controls: on }));
      onChange?.();
    }
  };

  const uploadLabel = progress === null ? 'Upload a video' : `Uploading… ${progress}%`;

  const applyUrl = (url) => {
    setImgError('');
    if (replaceImage(element, url)) {
      setV((cur) => ({ ...cur, src: url }));
      onChange?.();
    } else if (url.trim()) {
      setImgError('Use a full https:// address, or upload a file.');
    }
  };

  // Typing into the node itself works — it is contenteditable — but only if you
  // can see and hit it. On a small button, a pale caption, or anything the
  // preview has scrolled half out of view, the bar is the reliable way in. It
  // showed `a.btn.btn-secondary` there instead: a correct description of the
  // selection and no help at all with the thing you double-clicked to change.
  const setText = (value) => {
    element.textContent = value;
    setV((cur) => ({ ...cur, text: value }));
    onChange?.();
  };

  const trail = ancestorTrail(element);
  const isBold = Number(v.fontWeight) >= 600;

  // Upload, or paste a link: the same fields under "Replace video" and under
  // "Replace image", where a picture can become a video.
  const videoFields = (current) => (
    <div className="space-y-2">
      <button
        onClick={pickVideoFile}
        disabled={uploading}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-dashed border-zinc-700 text-xs text-zinc-300 hover:border-orange-500 hover:text-white transition-colors disabled:opacity-60"
      >
        <Upload className="w-3.5 h-3.5" />
        {uploadLabel}
      </button>
      {progress !== null && (
        <div className="h-1 rounded-full bg-zinc-800 overflow-hidden" role="progressbar" aria-label="Video upload"
          aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
          <div className="h-full rounded-full bg-orange-500 transition-[width] duration-200" style={{ width: `${progress}%` }} />
        </div>
      )}
      <input
        type="text"
        defaultValue={current}
        aria-label="Video link"
        className={FIELD}
        placeholder="YouTube, Vimeo or https://…/clip.mp4"
        onKeyDown={(e) => { if (e.key === 'Enter') applyVideoLink(e.currentTarget.value); }}
        onBlur={(e) => applyVideoLink(e.currentTarget.value)}
      />
      <p className="text-[10px] text-zinc-500 leading-relaxed">
        MP4, WebM or MOV, up to {MAX_VIDEO_MB} MB. A longer film is better on YouTube or Vimeo: paste its link.
      </p>
      {vidError && <p role="alert" className="text-[11px] text-red-400">{vidError}</p>}
    </div>
  );

  return (
    <div className="border-t border-zinc-800 bg-zinc-900">
      {/* Outside the popovers, so it is still there to answer when the
          system's file chooser closes, whatever the popover did meanwhile. */}
      <input ref={videoRef} type="file" accept={VIDEO_ACCEPT} onChange={onVideoFile} className="hidden" aria-label="Upload a video" />
      <div className="flex items-center gap-1.5 px-2.5 py-2 overflow-x-auto custom-scrollbar">

        {/* Replace the picture. Only shown on an image, and deliberately on the
            bar rather than inside the popover: it is the commonest edit there
            is on a generated site. */}
        {isImage(element) && (
          <Popover>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-zinc-700 text-xs text-zinc-200 hover:border-orange-500 hover:text-white transition-colors shrink-0">
                <ImageIcon className="w-3.5 h-3.5" />
                Replace image
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 bg-zinc-900 border-zinc-800 p-3 space-y-3">
              <div>
                <label className={LABEL}>From your computer</label>
                <button
                  onClick={pickFile}
                  disabled={uploading}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-dashed border-zinc-700 text-xs text-zinc-300 hover:border-orange-500 hover:text-white transition-colors disabled:opacity-60"
                >
                  <Upload className="w-3.5 h-3.5" />
                  {uploading ? 'Uploading…' : 'Choose a file'}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={onFile}
                  className="hidden"
                />
                <p className="text-[10px] text-zinc-500 mt-1.5 leading-relaxed">
                  Resized to 1600px and uploaded, up to 4 MB. It does not count toward the page&apos;s size.
                </p>
              </div>

              <div>
                <label className={LABEL}>Or paste a link</label>
                <input
                  type="text"
                  defaultValue={v.src || ''}
                  className={FIELD}
                  placeholder="https://…/photo.jpg"
                  onKeyDown={(e) => { if (e.key === 'Enter') applyUrl(e.currentTarget.value); }}
                  onBlur={(e) => applyUrl(e.currentTarget.value)}
                />
              </div>

              {imgError && <p className="text-[11px] text-red-400">{imgError}</p>}

              {/* A picture can become a video: a hero photograph a moving one,
                  a gallery tile a showreel. */}
              <div className="border-t border-zinc-800 pt-3">
                <label className={LABEL}><Film className="w-3 h-3 inline mr-1" />Or use a video instead</label>
                {videoFields('')}
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Replace the clip, or the player. The counterpart to Replace image,
            and on the bar for the same reason: on a generated site the video
            is chosen by a search phrase, so it is right about as often as the
            photographs. */}
        {(isVideo(element) || isEmbed(element)) && (
          <Popover>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-zinc-700 text-xs text-zinc-200 hover:border-orange-500 hover:text-white transition-colors shrink-0">
                <Film className="w-3.5 h-3.5" />
                Replace video
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 bg-zinc-900 border-zinc-800 p-3 space-y-3">
              <div>
                <label className={LABEL}>Upload a video, or paste a link</label>
                {videoFields(v.videoSrc || '')}
              </div>

              {isVideo(element) && (
                <label className="flex items-start gap-2 border-t border-zinc-800 pt-3 text-xs text-zinc-300 cursor-pointer">
                  <input type="checkbox" checked={!!v.controls} onChange={(e) => toggleControls(e.target.checked)}
                    className="mt-0.5 accent-orange-500" />
                  <span>
                    Play with sound and controls
                    <span className="block text-[10px] text-zinc-500 mt-0.5">Off, it plays silently on a loop, like a moving picture.</span>
                  </span>
                </label>
              )}
            </PopoverContent>
          </Popover>
        )}

        {/* What is selected, and the way back out of it — unless what is
            selected is text, in which case the useful thing to put here is the
            text. */}
        {v.text !== null && v.text !== undefined ? (
          <input
            type="text"
            value={v.text}
            onChange={(e) => setText(e.target.value)}
            title={describe(element)}
            aria-label={`Text of ${describe(element)}`}
            className="min-w-[160px] max-w-[280px] shrink-0 bg-zinc-950 border border-zinc-700 rounded px-2 py-1
                       text-xs text-zinc-100 focus:outline-none focus:border-orange-500"
          />
        ) : (
          <span
            className="text-[11px] font-mono text-orange-400 truncate max-w-[140px] shrink-0"
            title={describe(element)}
          >
            {describe(element)}
          </span>
        )}
        {element.parentElement && (
          <button className={ICON_BTN} title="Select parent"
                  onClick={() => onSelectElement?.(element.parentElement)}>
            <CornerUpLeft className="w-3.5 h-3.5" />
          </button>
        )}

        <span className="w-px h-5 bg-zinc-800 shrink-0" />

        {/* The controls worth a permanent seat: the ones used on almost every
            selection. Everything else is behind the sliders button. */}
        <button
          className={`${ICON_BTN} ${isBold ? 'border-orange-500 text-orange-400' : ''}`}
          title="Bold"
          onClick={() => set('fontWeight', 'font-weight', isBold ? '400' : '700')}
        >
          <Bold className="w-3.5 h-3.5" />
        </button>
        {[['left', AlignLeft], ['center', AlignCenter], ['right', AlignRight]].map(([a, Icon]) => (
          <button
            key={a}
            className={`${ICON_BTN} ${v.align === a ? 'border-orange-500 text-orange-400' : ''}`}
            title={`Align ${a}`}
            onClick={() => set('align', 'text-align', a)}
          >
            <Icon className="w-3.5 h-3.5" />
          </button>
        ))}

        <label className="relative shrink-0" title="Text colour">
          <input type="color" value={v.color || '#000000'} className={SWATCH}
                 onChange={(e) => set('color', 'color', e.target.value)} />
        </label>
        <label className="relative shrink-0" title="Background colour">
          <input type="color" value={v.background || '#ffffff'} className={SWATCH}
                 onChange={(e) => set('background', 'background-color', e.target.value)} />
        </label>

        <span className="w-px h-5 bg-zinc-800 shrink-0" />

        {/* Size, spacing, link and image target: real controls, just not ones
            worth half the preview. */}
        <Popover>
          <PopoverTrigger asChild>
            <button className={ICON_BTN} title="Size, spacing and more">
              <SlidersHorizontal className="w-3.5 h-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" sideOffset={10}
                          className="w-64 bg-zinc-950 border-zinc-800 p-3 space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className={LABEL}>Size</label>
                <input type="number" min="8" max="200" value={v.fontSize ?? ''} className={FIELD}
                       onChange={(e) => set('fontSize', 'font-size', e.target.value, 'px')} />
              </div>
              <div>
                <label className={LABEL}>Padding</label>
                <input type="number" min="0" max="200" value={v.padding ?? ''} className={FIELD}
                       onChange={(e) => set('padding', 'padding', e.target.value, 'px')} />
              </div>
              <div>
                <label className={LABEL}>Radius</label>
                <input type="number" min="0" max="200" value={v.radius ?? ''} className={FIELD}
                       onChange={(e) => set('radius', 'border-radius', e.target.value, 'px')} />
              </div>
            </div>

            {v.href !== null && v.href !== undefined && (
              <div>
                <label className={LABEL}><Link2 className="w-3 h-3 inline mr-1" />Link</label>
                <input type="text" value={v.href} className={FIELD} placeholder="about.html or https://…"
                       onChange={(e) => setAttr('href', 'href', e.target.value)} />
              </div>
            )}

            {v.src !== null && v.src !== undefined && (
              <div>
                <label className={LABEL}><ImageIcon className="w-3 h-3 inline mr-1" />Image</label>
                <input type="text" value={v.src} className={FIELD} placeholder="https://…/photo.jpg"
                       onChange={(e) => setV((cur) => ({ ...cur, src: e.target.value }))}
                       onBlur={(e) => applyUrl(e.currentTarget.value)} />
              </div>
            )}

            {trail.length > 0 && (
              <div>
                <label className={LABEL}>Select a parent</label>
                <div className="flex flex-wrap gap-1">
                  {trail.map((a, i) => (
                    <button
                      key={i}
                      onClick={() => onSelectElement?.(a)}
                      className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600"
                    >
                      {describe(a)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </PopoverContent>
        </Popover>

        <button
          onClick={() => { const copy = duplicateElement(element); onChange?.(); if (copy) onSelectElement?.(copy); }}
          className={ICON_BTN}
          title="Duplicate"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => { const parent = removeElement(element); onChange?.(); onSelectElement?.(parent); }}
          className="p-1.5 rounded-md border border-red-900/60 text-red-400 hover:border-red-700 transition-colors shrink-0"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>

        {/* Pushed right so Deselect is always in the same place regardless of
            how many controls the selection happens to offer. */}
        <div className="flex-1 min-w-[8px]" />

        {holdsOnlyText(element) && (
          <span className="hidden md:flex items-center gap-1 text-[10px] text-emerald-400/80 shrink-0 mr-1">
            <Pencil className="w-3 h-3" /> Click into the preview to type
          </span>
        )}

        <button className={ICON_BTN} title="Deselect" onClick={onClose}>
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
